export const LEAD_SOURCE_URL_MAX_LENGTH = 255

// `aqt` is the email-link visitor token: the site strips it from the address bar
// before any tag loads, but it must never be persisted to Salesforce if one slips
// through (it embeds the plaintext Contact Id).
const NOISY_QUERY_PARAMS = ['sftoken', 'aqt'] as const

/**
 * Prepares a page URL for the Salesforce `Lead_Source_URL__c` field, which has
 * a 255-char limit. Strips every SENSITIVE_QUERY_PARAMS name (Sales Cloud
 * `sftoken` JWTs, `?email=` prefill, the `aqt` visitor token), matched without
 * regard to case, and hard-truncates the result as a defence-in-depth safety
 * net. This runs server-side for every form, so a form that sends
 * `window.location.href` unchanged is covered too.
 */
export function sanitizePageUrl(pageUrl: string | undefined): string | undefined {
  if (!pageUrl) return undefined

  let cleaned = pageUrl
  try {
    const url = new URL(pageUrl)
    for (const key of [...url.searchParams.keys()]) {
      if (SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())) {
        url.searchParams.delete(key)
      }
    }
    cleaned = url.toString()
  } catch {
    // Not a parsable URL; fall through to plain truncation.
  }

  if (cleaned.length > LEAD_SOURCE_URL_MAX_LENGTH) {
    cleaned = cleaned.slice(0, LEAD_SOURCE_URL_MAX_LENGTH)
  }

  return cleaned
}

/**
 * Query params that carry personal data (e.g. ?email= prefill from email
 * blasts). Stripped before a page URL is recorded as Lead_Source_URL__c
 * (HeroFormContent) and before it is sent to Data Cloud as `source.url`.
 */
export const PII_QUERY_PARAMS = ['email'] as const

/**
 * Every query param name that must never be recorded in a page URL: the noisy
 * or secret tokens above plus the personal-data params. `aqt` (the email-link
 * visitor token, which embeds a Contact Id) is named here too, so the list is
 * right even before it is added to NOISY_QUERY_PARAMS.
 */
export const SENSITIVE_QUERY_PARAMS: readonly string[] = [
  ...new Set<string>([...NOISY_QUERY_PARAMS, ...PII_QUERY_PARAMS, 'aqt']),
]
